import ContentCopy from "@mui/icons-material/ContentCopy";
import { IconButton, Tooltip } from "@mui/material";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { DuplicateAssetDialog } from "../../../assets/components/duplicate-asset-dialog";
import { useDuplicateAnyAssetCallback } from "../../../assets/hooks/use-duplicate-asset-callback";
import {
  SupabaseDigitalAssetService,
  toMaybeAsset,
} from "../../../assets/server/digital-asset-service";

import type { Asset } from "../../../assets/server/digital-asset-service";
import type { RecentlyEditedItem } from "../../hooks/use-recently-edited";
import type { FC } from "react";

/** The copy as a row of the feed, in its original's folder. */
const asRow = (copy: Asset, original: RecentlyEditedItem): RecentlyEditedItem => ({
  ...original,
  id: copy.id,
  title: copy.title,
  last_edited_at: copy.updated_at,
  content_type: copy.content_type,
  public_url: copy.public_url,
  mime_type: copy.mime_type,
  thumbnail_url: copy.thumbnail_url,
});

/**
 * Duplicates a Digital Asset in place, as the Content Library does, and hands the copy to
 * `onDuplicated` to show; on failure, says so and stays open to try again.
 */
export const DuplicateAssetButton: FC<{
  asset: RecentlyEditedItem;
  onDuplicated: (copy: RecentlyEditedItem) => void;
}> = ({ asset, onDuplicated }) => {
  const dialog = useDialog();
  const [duplicating, setDuplicating] = useState(false);
  const duplicateAsset = useDuplicateAnyAssetCallback();

  // The feed's row lacks what copying needs: the project, its page and the folder.
  const loadAsset = useSupabaseCallback(
    async ({ supabase }) => {
      const { data, error } = await new SupabaseDigitalAssetService(supabase).get(asset.id);
      if (error) throw new Error(error.message);
      const full = toMaybeAsset(data);
      if (!full) throw new Error(`Asset ${asset.id} not found`);
      return full;
    },
    [asset.id],
  );

  const duplicate = async (): Promise<void> => {
    setDuplicating(true);
    try {
      const full = await loadAsset();
      if (!full) throw new Error("Not signed in");
      const copy = await duplicateAsset(full);
      toast.success(`${asset.title} duplicated`);
      dialog.handleClose();
      onDuplicated(asRow(copy, asset));
    } catch (error) {
      console.error(error);
      toast.error(`${asset.title} failed to duplicate, please try again.`);
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <>
      <Tooltip title={`Duplicate: ${asset.title}`}>
        <IconButton
          data-analytics-id="dashboard-recently-edited-asset-duplicate"
          onClick={dialog.handleOpen}
        >
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
      <DuplicateAssetDialog
        asset={{ title: asset.title, content_type: asset.content_type ?? "" }}
        open={dialog.open}
        duplicating={duplicating}
        onClose={dialog.handleClose}
        onConfirm={() => void duplicate()}
      />
    </>
  );
};
